(function () {
  'use strict';

  const formatMoney = (cents, format) => {
    const value = Number.isFinite(Number(cents)) ? Number(cents) : 0;
    const template = format || '${{amount}}';
    const match = template.match(/\{\{\s*(\w+)\s*\}\}/);
    const placeholder = match ? match[1] : 'amount';
    const withDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      const normalized = (Number(number) / 100).toFixed(precision);
      const parts = normalized.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts[1] ? parts.join(decimal) : parts[0];
    };

    let output;
    switch (placeholder) {
      case 'amount_no_decimals':
        output = withDelimiters(value, 0);
        break;
      case 'amount_with_comma_separator':
        output = withDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        output = withDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        output = withDelimiters(value, 2, "'", '.');
        break;
      default:
        output = withDelimiters(value, 2);
    }
    return template.replace(match ? match[0] : '{{amount}}', output);
  };

  class CornerFitBundleSelector extends HTMLElement {
    constructor() {
      super();
      this.onChange = this.onChange.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onUnitOptionChange = this.onUnitOptionChange.bind(this);
    }

    connectedCallback() {
      this.radios = Array.from(this.querySelectorAll('input[type="radio"][data-cf-bundle-radio]'));
      this.radios.forEach((radio) => radio.addEventListener('change', this.onChange));
      this.form = document.getElementById(this.dataset.productFormId);
      this.variantsPanel = this.querySelector('[data-cf-bundle-variants]');
      this.unitRows = Array.from(this.querySelectorAll('[data-cf-bundle-unit]'));
      this.unitSelects = Array.from(this.querySelectorAll('[data-cf-bundle-option]'));
      this.unitSelects.forEach((select) => select.addEventListener('change', this.onUnitOptionChange));
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.variant = this.readInitialVariant();
      this.variants = this.readProductVariants();
      this.hasVariants = this.dataset.hasVariants === 'true' && this.variants.length > 1;
      this.unsubscribe = typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined'
        ? subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange)
        : null;

      const checked = this.radios.find((radio) => radio.checked) || this.radios[0];
      if (checked) this.activate(checked, false);
      this.updatePrices();
    }

    disconnectedCallback() {
      this.radios?.forEach((radio) => radio.removeEventListener('change', this.onChange));
      this.unitSelects?.forEach((select) => select.removeEventListener('change', this.onUnitOptionChange));
      this.clearBundleFormData();
      if (typeof this.unsubscribe === 'function') this.unsubscribe();
    }

    readInitialVariant() {
      try {
        return JSON.parse(this.querySelector('[data-cf-selected-variant]')?.textContent || '{}');
      } catch (_error) {
        return {};
      }
    }

    readProductVariants() {
      try {
        const variants = JSON.parse(this.querySelector('[data-cf-product-variants]')?.textContent || '[]');
        return Array.isArray(variants) ? variants : [];
      } catch (_error) {
        return [];
      }
    }

    onVariantChange(event) {
      if (String(event?.data?.sectionId) !== String(this.dataset.sectionId)) return;
      if (!event.data.variant) return;
      this.variant = event.data.variant;
      if (this.variantsPanel && !this.variantsPanel.hidden) {
        this.getVisibleRows().forEach((row) => {
          this.setRowToVariant(row, this.variant);
          this.updateRowVariant(row);
        });
        this.syncFormBundleItems();
      }
      this.updatePrices();
    }

    onChange(event) {
      this.activate(event.currentTarget, true);
    }

    activate(radio, emitEvent) {
      const selectedOffer = radio.closest('[data-cf-offer]');
      this.querySelectorAll('.cf-bundle__offer').forEach((offer) => {
        const selected = offer === selectedOffer;
        offer.classList.toggle('is-selected', selected);
        offer.setAttribute('aria-selected', selected ? 'true' : 'false');
      });

      const quantity = Math.max(1, parseInt(radio.dataset.quantity, 10) || 1);
      this.setProductQuantity(quantity);
      this.syncVariantPanel(selectedOffer, quantity);
      this.updatePrices();
      if (emitEvent) {
        this.dispatchEvent(new CustomEvent('cornerfit:bundle-change', {
          bubbles: true,
          detail: { quantity, tier: radio.value },
        }));
      }
    }

    setProductQuantity(quantity) {
      const formId = this.dataset.productFormId;
      const selector = [
        `input[name="quantity"][form="${CSS.escape(formId)}"]`,
        `#${CSS.escape(formId)} input[name="quantity"]`,
      ].join(',');
      const inputs = Array.from(document.querySelectorAll(selector));
      inputs.forEach((input) => {
        input.value = quantity;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    syncVariantPanel(selectedOffer, quantity) {
      if (!this.variantsPanel || !this.hasVariants || quantity < 2 || !selectedOffer) {
        if (this.variantsPanel) this.variantsPanel.hidden = true;
        this.clearBundleFormData();
        return;
      }

      selectedOffer.appendChild(this.variantsPanel);
      this.variantsPanel.hidden = false;
      this.unitRows.forEach((row, index) => {
        const visible = index < quantity;
        row.hidden = !visible;
        if (!visible) return;
        if (row.dataset.initialized !== 'true') this.setRowToVariant(row, this.variant);
        this.updateRowVariant(row);
      });
      this.syncFormBundleItems();
    }

    setRowToVariant(row, variant) {
      if (!row || !variant || !Array.isArray(variant.options)) return;
      row.querySelectorAll('[data-cf-bundle-option]').forEach((select) => {
        const position = Math.max(0, parseInt(select.dataset.optionPosition, 10) - 1);
        if (typeof variant.options[position] !== 'undefined') select.value = variant.options[position];
      });
      row.dataset.initialized = 'true';
      this.updateOptionAvailability(row);
    }

    onUnitOptionChange(event) {
      const row = event.currentTarget.closest('[data-cf-bundle-unit]');
      if (!row) return;
      this.updateOptionAvailability(row);
      this.updateRowVariant(row);
      this.syncFormBundleItems();
      this.updatePrices();
    }

    getRowValues(row) {
      return Array.from(row.querySelectorAll('[data-cf-bundle-option]'))
        .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
        .map((select) => select.value);
    }

    findVariant(values) {
      return this.variants.find((variant) => Array.isArray(variant.options)
        && variant.options.length === values.length
        && variant.options.every((value, index) => value === values[index]));
    }

    updateOptionAvailability(row) {
      const selects = Array.from(row.querySelectorAll('[data-cf-bundle-option]'))
        .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition));
      const selectedPrefix = [];

      selects.forEach((select, selectIndex) => {
        Array.from(select.options).forEach((option) => {
          const available = this.variants.some((variant) => variant.available
            && Array.isArray(variant.options)
            && variant.options[selectIndex] === option.value
            && variant.options.slice(0, selectIndex).every((value, index) => value === selectedPrefix[index]));
          option.disabled = !available;
        });

        if (select.selectedOptions[0]?.disabled) {
          const firstAvailable = Array.from(select.options).find((option) => !option.disabled);
          if (firstAvailable) select.value = firstAvailable.value;
        }
        selectedPrefix.push(select.value);
      });
    }

    updateRowVariant(row) {
      const variant = this.findVariant(this.getRowValues(row));
      const available = Boolean(variant?.available);
      row.cfVariant = variant || null;
      row.classList.toggle('is-unavailable', !available);
      row.setAttribute('aria-invalid', available ? 'false' : 'true');
      const error = row.querySelector('[data-cf-bundle-unit-error]');
      if (error) error.hidden = available;
      if (available) row.dataset.variantId = String(variant.id);
      else delete row.dataset.variantId;
      return available;
    }

    getVisibleRows() {
      return this.unitRows.filter((row) => !row.hidden);
    }

    syncFormBundleItems() {
      if (!this.form) this.form = document.getElementById(this.dataset.productFormId);
      if (!this.form || !this.variantsPanel || this.variantsPanel.hidden) {
        this.clearBundleFormData();
        return;
      }

      const rows = this.getVisibleRows();
      const valid = rows.length > 0 && rows.every((row) => this.updateRowVariant(row));
      if (!valid) {
        this.form.cfBundleItems = [];
        this.form.dataset.cfBundleInvalid = 'true';
        this.form.dataset.cfBundleError = this.dataset.bundleError || 'Please choose an available option for every item.';
        return;
      }

      const grouped = new Map();
      rows.forEach((row) => {
        const id = Number(row.cfVariant.id);
        const item = grouped.get(id) || { id, quantity: 0 };
        item.quantity += 1;
        grouped.set(id, item);
      });

      this.form.cfBundleItems = Array.from(grouped.values());
      this.form.dataset.cfBundleOffer = this.querySelector('[data-cf-offer].is-selected')?.dataset.offerTitle || 'Bundle & Save';
      delete this.form.dataset.cfBundleInvalid;
      delete this.form.dataset.cfBundleError;
    }

    clearBundleFormData() {
      if (!this.form) return;
      delete this.form.cfBundleItems;
      delete this.form.dataset.cfBundleInvalid;
      delete this.form.dataset.cfBundleError;
      delete this.form.dataset.cfBundleOffer;
    }

    updatePrices() {
      if (!this.variant || typeof this.variant.price === 'undefined') return;
      const unitPrice = Number(this.variant.price) || 0;
      const compareUnit = Number(this.variant.compare_at_price) > unitPrice
        ? Number(this.variant.compare_at_price)
        : unitPrice;

      this.querySelectorAll('[data-cf-offer]').forEach((offer) => {
        const quantity = Math.max(1, parseInt(offer.dataset.quantity, 10) || 1);
        const selectedRows = offer.classList.contains('is-selected') && this.variantsPanel && !this.variantsPanel.hidden
          ? this.getVisibleRows().map((row) => row.cfVariant).filter(Boolean)
          : [];
        const hasCompleteSelection = selectedRows.length === quantity;
        const subtotal = hasCompleteSelection
          ? selectedRows.reduce((sum, rowVariant) => sum + (Number(rowVariant.price) || 0), 0)
          : unitPrice * quantity;
        const percentageOff = Math.max(0, Number(offer.dataset.percentageOff) || 0);
        const fixedOff = Math.max(0, Number(offer.dataset.fixedOff) || 0);
        const discount = Math.round(subtotal * percentageOff / 100) + Math.round(fixedOff * 100);
        const total = Math.max(0, subtotal - discount);
        const compareTotal = offer.dataset.compareBasis === 'price'
          ? subtotal
          : hasCompleteSelection
            ? selectedRows.reduce((sum, rowVariant) => {
              const rowPrice = Number(rowVariant.price) || 0;
              const rowCompare = Number(rowVariant.compare_at_price) > rowPrice
                ? Number(rowVariant.compare_at_price)
                : rowPrice;
              return sum + rowCompare;
            }, 0)
            : compareUnit * quantity;
        const priceNode = offer.querySelector('[data-cf-total]');
        const compareNode = offer.querySelector('[data-cf-compare]');

        if (priceNode) priceNode.textContent = formatMoney(total, this.moneyFormat);
        if (compareNode) {
          const shouldShow = this.dataset.showCompare === 'true' && compareTotal > total;
          compareNode.textContent = formatMoney(compareTotal, this.moneyFormat);
          compareNode.hidden = !shouldShow;
        }
      });
    }
  }

  class CornerFitBundleAddon extends HTMLElement {
    constructor() {
      super();
      this.onChange = this.onChange.bind(this);
    }

    connectedCallback() {
      this.form = document.getElementById(this.dataset.productFormId);
      this.toggle = this.querySelector('[data-cf-addon-toggle]');
      this.variantControl = this.querySelector('[data-cf-addon-variant]');
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.toggle?.addEventListener('change', this.onChange);
      this.variantControl?.addEventListener('change', this.onChange);
      this.onChange();
    }

    disconnectedCallback() {
      this.toggle?.removeEventListener('change', this.onChange);
      this.variantControl?.removeEventListener('change', this.onChange);
      this.clearFormData();
    }

    getSelectedVariantData() {
      if (!this.variantControl) return null;
      if (this.variantControl.tagName === 'SELECT') {
        const option = this.variantControl.selectedOptions[0];
        if (!option) return null;
        return {
          id: option.value,
          price: Number(option.dataset.price) || 0,
          available: option.dataset.available === 'true',
          image: option.dataset.image || '',
        };
      }
      return {
        id: this.variantControl.value,
        price: Number(this.variantControl.dataset.price) || 0,
        available: this.variantControl.dataset.available === 'true',
        image: this.variantControl.dataset.image || '',
      };
    }

    onChange() {
      const variant = this.getSelectedVariantData();
      const included = this.dataset.addonMode === 'included';
      const selected = included || Boolean(this.toggle?.checked);
      const enabled = Boolean(selected && variant?.available && variant.id);
      this.classList.toggle('is-selected', enabled);
      this.classList.toggle('is-unavailable', Boolean(variant && !variant.available));

      const price = this.querySelector('[data-cf-addon-price]');
      if (price && variant) price.textContent = formatMoney(variant.price, this.moneyFormat);
      const image = this.querySelector('[data-cf-addon-media] img');
      if (image && variant?.image) image.src = variant.image;

      if (!this.form) this.form = document.getElementById(this.dataset.productFormId);
      if (enabled && this.form) {
        this.form.dataset.cfAddonVariantId = variant.id;
        this.form.dataset.cfAddonQuantity = String(Math.max(1, parseInt(this.dataset.addonQuantity, 10) || 1));
        this.form.dataset.cfAddonPropertyLabel = this.dataset.propertyLabel || 'Bundle add-on';
      } else {
        this.clearFormData();
      }

      this.dispatchEvent(new CustomEvent('cornerfit:addon-change', {
        bubbles: true,
        detail: { selected: enabled, variantId: variant?.id || null },
      }));
    }

    clearFormData() {
      if (!this.form) return;
      delete this.form.dataset.cfAddonVariantId;
      delete this.form.dataset.cfAddonQuantity;
      delete this.form.dataset.cfAddonPropertyLabel;
    }
  }

  class CornerFitInlineReviews extends HTMLElement {
    constructor() {
      super();
      this.index = 0;
      this.showPrevious = () => this.show(this.index - 1, true);
      this.showNext = () => this.show(this.index + 1, true);
      this.pause = this.pause.bind(this);
      this.play = this.play.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('[data-cf-review-track]');
      this.slides = Array.from(this.querySelectorAll('[data-cf-review-slide]'));
      this.previousButton = this.querySelector('[data-cf-review-prev]');
      this.nextButton = this.querySelector('[data-cf-review-next]');
      if (!this.track || this.slides.length === 0) return;

      this.previousButton?.addEventListener('click', this.showPrevious);
      this.nextButton?.addEventListener('click', this.showNext);
      this.addEventListener('mouseenter', this.pause);
      this.addEventListener('mouseleave', this.play);
      this.addEventListener('focusin', this.pause);
      this.addEventListener('focusout', this.play);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.show(0, false);
      this.play();
    }

    disconnectedCallback() {
      this.previousButton?.removeEventListener('click', this.showPrevious);
      this.nextButton?.removeEventListener('click', this.showNext);
      this.removeEventListener('mouseenter', this.pause);
      this.removeEventListener('mouseleave', this.play);
      this.removeEventListener('focusin', this.pause);
      this.removeEventListener('focusout', this.play);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.pause();
    }

    show(nextIndex, restartAutoplay) {
      const count = this.slides.length;
      this.index = ((nextIndex % count) + count) % count;
      this.track.style.transform = `translate3d(-${this.index * 100}%, 0, 0)`;
      this.slides.forEach((slide, index) => slide.setAttribute('aria-hidden', index === this.index ? 'false' : 'true'));
      if (restartAutoplay) {
        this.pause();
        this.play();
      }
    }

    play() {
      if (this.dataset.autoplay !== 'true' || this.slides?.length < 2 || document.hidden) return;
      this.pause();
      const speed = Math.max(3000, Number(this.dataset.speed) || 5000);
      this.timer = window.setInterval(() => this.show(this.index + 1, false), speed);
    }

    pause() {
      window.clearInterval(this.timer);
      this.timer = null;
    }

    onVisibilityChange() {
      if (document.hidden) this.pause();
      else this.play();
    }
  }

  class CornerFitCountdown extends HTMLElement {
    connectedCallback() {
      this.duration = Math.max(1, parseInt(this.dataset.durationMinutes, 10) || 1440) * 60 * 1000;
      this.endTime = this.getEndTime();
      this.render();
      this.timer = window.setInterval(() => this.render(), 1000);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    getEndTime() {
      if (this.dataset.mode !== 'evergreen') return Date.now() + this.duration;
      const key = `cornerfit-countdown-${this.dataset.storageKey || 'offer'}`;
      try {
        const stored = Number(localStorage.getItem(key));
        if (stored > Date.now()) return stored;
        const next = Date.now() + this.duration;
        localStorage.setItem(key, String(next));
        return next;
      } catch (_error) {
        return Date.now() + this.duration;
      }
    }

    render() {
      let remaining = Math.max(0, this.endTime - Date.now());
      if (remaining === 0 && this.dataset.restart === 'true') {
        this.endTime = Date.now() + this.duration;
        remaining = this.duration;
      }
      const totalSeconds = Math.floor(remaining / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      this.setDigits('hours', hours);
      this.setDigits('minutes', minutes);
      this.setDigits('seconds', seconds);
    }

    setDigits(part, value) {
      const output = String(value).padStart(2, '0').slice(-2);
      this.querySelectorAll(`[data-cf-time="${part}"]`).forEach((node, index) => {
        node.textContent = output[index];
      });
    }
  }

  class CornerFitStickyAtc extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
    }

    connectedCallback() {
      this.button = this.querySelector('[data-cf-sticky-submit]');
      this.button?.addEventListener('click', this.onClick);
      this.unsubscribe = typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined'
        ? subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange)
        : null;
      requestAnimationFrame(() => this.setupMainButton());
    }

    setupMainButton() {
      if (this.mainButton) return;
      this.mainButton = document.getElementById(`ProductSubmitButton-${this.dataset.sectionId}`);
      if (this.mainButton && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(([entry]) => {
          this.classList.toggle('is-visible', !entry.isIntersecting && window.scrollY > 240);
        }, { threshold: 0.05 });
        this.observer.observe(this.mainButton);
        this.syncDisabledState();
        this.mutationObserver = new MutationObserver(() => this.syncDisabledState());
        this.mutationObserver.observe(this.mainButton, { attributes: true, childList: true, subtree: true });
      } else if (!this.mainButton) {
        this.retryCount = (this.retryCount || 0) + 1;
        if (this.retryCount < 20) this.retryTimer = window.setTimeout(() => this.setupMainButton(), 250);
      }
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.onClick);
      this.observer?.disconnect();
      this.mutationObserver?.disconnect();
      window.clearTimeout(this.retryTimer);
      if (typeof this.unsubscribe === 'function') this.unsubscribe();
    }

    syncDisabledState() {
      if (!this.button || !this.mainButton) return;
      const disabled = this.mainButton.disabled || this.mainButton.getAttribute('aria-disabled') === 'true';
      this.button.disabled = disabled;
      const sourceText = this.mainButton.querySelector('span:not(.sold-out-message)')?.textContent?.trim();
      if (sourceText) this.button.querySelector('span').textContent = sourceText;
    }

    onClick() {
      const form = document.getElementById(this.dataset.productFormId);
      if (!form || !this.mainButton || this.button.disabled) return;
      form.requestSubmit(this.mainButton);
    }

    onVariantChange(event) {
      if (String(event?.data?.sectionId) !== String(this.dataset.sectionId)) return;
      const variant = event.data.variant;
      if (!variant) return;
      const price = this.querySelector('[data-cf-sticky-price]');
      const variantTitle = this.querySelector('[data-cf-sticky-variant]');
      const image = this.querySelector('[data-cf-sticky-image]');
      if (price) price.textContent = formatMoney(Number(variant.price) || 0, this.dataset.moneyFormat);
      if (variantTitle) variantTitle.textContent = variant.title === 'Default Title' ? '' : variant.title;
      const source = variant.featured_image?.src || variant.featured_media?.preview_image?.src;
      if (image && source) image.src = `${source}${source.includes('?') ? '&' : '?'}width=120`;
    }
  }

  if (!customElements.get('cf-bundle-selector')) {
    customElements.define('cf-bundle-selector', CornerFitBundleSelector);
  }
  if (!customElements.get('cf-bundle-addon')) {
    customElements.define('cf-bundle-addon', CornerFitBundleAddon);
  }
  if (!customElements.get('cf-inline-reviews')) {
    customElements.define('cf-inline-reviews', CornerFitInlineReviews);
  }
  if (!customElements.get('cf-countdown')) {
    customElements.define('cf-countdown', CornerFitCountdown);
  }
  if (!customElements.get('cf-sticky-atc')) {
    customElements.define('cf-sticky-atc', CornerFitStickyAtc);
  }

  if (!window.cornerFitOptionLabelsBound) {
    window.cornerFitOptionLabelsBound = true;
    document.addEventListener('change', (event) => {
      const control = event.target;
      if (!(control instanceof HTMLSelectElement) || !control.closest('variant-selects')) return;
      const selectedLabel = control.closest('.product-form__input')?.querySelector('.cf-option-label__selected');
      if (selectedLabel) selectedLabel.textContent = control.value;
    });
  }
})();
